import { loadJsPDF } from "./pdfEngines";
import { addPreviewWatermarkJsPdf } from "./previewWatermark";
import { generateTemplateA, generateTemplateB, generateTemplateC, generateTemplateH } from "./paystubTemplates";
import { renderLayout, fetchPublishedLayout, fetchCustomLayout, isCustomTemplate, preloadLayoutAssets } from "./layoutEngine";
import { calculateFederalTax, calculateStateTax, getStateTaxRate } from "./federalTaxCalculator";
import { getEffectiveLocalTaxRate, getSUTARate } from "./taxRates";
import { usRates, fetchTaxRates } from "./taxRateDefaults";
import { loadPdfJs } from "./pdfEngines";

// A ready-to-render sample stub, so a card thumbnail (e.g. a business's own
// paystub template on its category page) shows a fully computed stub.
export const SAMPLE_PAYSTUB_DATA = {
  name: "John Smith", ssn: "1234", bank: "5678", bankName: "Chase Bank",
  address: "123 Main Street", city: "New York", state: "NY", zip: "10001",
  company: "Acme Corporation", companyAddress: "456 Business Ave",
  companyCity: "New York", companyState: "NY", companyZip: "10002",
  companyPhone: "(555) 123-4567",
  hireDate: "2022-01-01", startDate: "2025-01-06", endDate: "2025-01-19",
  rate: "25", payFrequency: "biweekly", payDay: "Friday",
  payType: "hourly", workerType: "employee", annualSalary: "",
  employeeId: "EMP001", companyCode: "AC001", locDept: "001", checkNumber: "1234567", memo: "",
  includeLocalTax: true, federalFilingStatus: "single", stateAllowances: "0",
  hoursList: "80", overtimeList: "0", commissionList: "0", tipsList: "0", tipsCashList: "0",
  startDateList: "2025-01-06", endDateList: "2025-01-19", payDateList: "2025-01-24",
  checkNumberList: "", memoList: "",
};

// Set up pdf.js worker using unpkg CDN with correct version

// PDF metadata configuration per template (matching real document signatures)
const TEMPLATE_METADATA = {
  'template-a': {  // Gusto template
    title: 'Gusto',
    creator: 'wkhtmltopdf 0.12.6.1',
    producer: 'Qt 4.8.7',
  },
  'template-b': {  // ADP template
    title: 'ADP',
    creator: 'wkhtmltopdf 0.12.6.1',
    producer: 'Qt 4.8.7',
  },
  'template-c': {  // Workday template - no title
    creator: 'Workday HCM',
    producer: 'Workday PDF Engine',
  },
  'template-h': {  // OnPay template - no title (same metadata as Gusto)
    creator: 'wkhtmltopdf 0.12.6.1',
    producer: 'Qt 4.8.7',
  },
};

// Apply template-specific PDF metadata - must be called right before output/save
function applyPdfMetadata(doc, template) {
  // Custom templates carry their own metadata (applied by renderLayout).
  if (isCustomTemplate(template)) return;
  const metadata = TEMPLATE_METADATA[template] || TEMPLATE_METADATA['template-a'];
  const props = {
    creator: metadata.creator,
    producer: metadata.producer,
  };
  if (metadata.title) {
    props.title = metadata.title;
  }
  doc.setProperties(props);
}

// Convert PDF to image using pdf.js
async function convertPdfToImage(pdfDataUrl) {
  // Convert base64 to ArrayBuffer
  const base64Data = pdfDataUrl.split(',')[1];
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  const loadingTask = (await loadPdfJs()).getDocument({ data: bytes.buffer });
  const pdf = await loadingTask.promise;
  
  // Get the first page
  const page = await pdf.getPage(1);
  
  // Set scale for good quality preview
  const scale = 2;
  const viewport = page.getViewport({ scale });
  
  // Create a canvas to render the PDF page
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Render the page to canvas
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;
  
  // Convert canvas to image data URL
  const imageDataUrl = canvas.toDataURL('image/png', 0.9);
  
  return imageDataUrl;
}

// Helper to calculate next weekday
const DAY_MAP = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

function nextWeekday(date, weekday) {
  const result = new Date(date);
  const target = DAY_MAP[weekday];
  if (target === undefined) return result;
  while (result.getDay() !== target) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}

// Helper function to calculate number of pay periods from hire date
function calculatePayPeriodsFromHireDate(hireDate, currentPeriodEnd, periodLength) {
  const payPeriodYear = currentPeriodEnd.getFullYear();
  const startOfYear = new Date(payPeriodYear, 0, 1);
  let ytdStartDate = hireDate > startOfYear ? hireDate : startOfYear;
  
  // IMPORTANT FIX: If ytdStartDate is AFTER the current period end, it means the hire date
  // is set incorrectly (after the pay periods). In this case, use start of year instead
  // to ensure YTD calculations make sense and accumulate properly.
  if (ytdStartDate > currentPeriodEnd) {
    ytdStartDate = startOfYear;
  }
  
  const diffTime = currentPeriodEnd.getTime() - ytdStartDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const numPeriods = Math.max(1, Math.ceil(diffDays / periodLength));
  console.log(`[Preview YTD Debug] hireDate=${hireDate?.toISOString()}, endDate=${currentPeriodEnd?.toISOString()}, ytdStartDate=${ytdStartDate?.toISOString()}, diffDays=${diffDays}, numPeriods=${numPeriods}`);
  return numPeriods;
}


// Helper function to generate a single stub preview
async function generateSingleStubPreview(formData, template, stubIndex, totalStubs, commonData) {
  const {
    rate, annualSalary, payFrequency, periodLength, defaultHours, payDay,
    payType, workerType, isContractor, periodsPerYear, hoursArray, overtimeArray,
    commissionArray, tipsArray, tipsCashArray, startDateArray, endDateArray, payDateArray, hireDate, stateRate, state
  } = commonData;

  const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Calculate start date for this stub
  let startDate;
  if (startDateArray[stubIndex]) {
    startDate = new Date(startDateArray[stubIndex] + 'T12:00:00');
  } else if (formData.startDate) {
    startDate = new Date(formData.startDate);
    // Move forward by stub index * period length
    startDate.setDate(startDate.getDate() + (stubIndex * periodLength));
  } else {
    startDate = new Date(hireDate);
    startDate.setDate(startDate.getDate() + (stubIndex * periodLength));
  }

  // Calculate pay values for this stub
  let hours = 0;
  let overtime = 0;
  let regularPay = 0;
  let overtimePay = 0;
  let commission = commissionArray[stubIndex] || 0;
  let tips = tipsArray[stubIndex] || 0;
  let tipsCash = tipsCashArray[stubIndex] || false;
  let grossPay = 0;
  
  // Cash tips are NOT included in gross pay (check amount)
  // But cash tips ARE included in taxable income for tax withholding purposes
  const tipsForPay = tipsCash ? 0 : tips;

  if (payType === "salary") {
    grossPay = annualSalary / periodsPerYear + commission + tipsForPay;
    regularPay = annualSalary / periodsPerYear;
    hours = defaultHours;
    overtime = 0;
    overtimePay = 0;
  } else {
    hours = hoursArray[stubIndex] || defaultHours;
    overtime = overtimeArray[stubIndex] || 0;
    regularPay = rate * hours;
    overtimePay = rate * 1.5 * overtime;
    grossPay = regularPay + overtimePay + commission + tipsForPay;
  }

  // Taxable income includes ALL tips (cash and non-cash) for tax calculation purposes
  // Even though cash tips aren't in the check, they must be taxed
  const taxableIncome = grossPay + (tipsCash ? tips : 0);

  // Taxes calculated on taxableIncome (includes cash tips)
  const rates = usRates(formData);
  const ssTax = isContractor ? 0 : taxableIncome * rates.ss;
  const medTax = isContractor ? 0 : taxableIncome * rates.med;
  
  let federalTax = 0;
  if (!isContractor) {
    if (formData.federalFilingStatus) {
      federalTax = calculateFederalTax(taxableIncome, payFrequency, formData.federalFilingStatus);
    } else {
      federalTax = taxableIncome * rates.fedFlat;
    }
  }
  
  let stateTax = 0;
  if (!isContractor) {
    stateTax = calculateStateTax(taxableIncome, formData.state, payFrequency, formData.stateAllowances || 0, stateRate);
  }
  
  const localTaxRate = getEffectiveLocalTaxRate(formData);
  const localTax = isContractor ? 0 : (formData.includeLocalTax && localTaxRate > 0 ? taxableIncome * localTaxRate : 0);
  const totalTax = ssTax + medTax + federalTax + stateTax + localTax;

  // Pre-tax deduction types
  const preTaxDeductionTypes = ['health_insurance', 'dental_insurance', 'vision_insurance'];
  const preTaxContributionTypes = ['401k', 'hsa', 'fsa', 'dependent_care_fsa', 'commuter'];

  // Calculate deductions
  const deductionsData = (formData.deductions || []).map(d => {
    const amount = parseFloat(d.amount) || 0;
    const currentAmount = d.isPercentage ? (grossPay * amount / 100) : amount;
    const isPreTax = d.preTax !== undefined ? d.preTax : preTaxDeductionTypes.includes(d.type);
    return { ...d, currentAmount, preTax: isPreTax, name: d.type === 'other' ? d.name : d.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) };
  });
  const totalDeductions = deductionsData.reduce((sum, d) => sum + d.currentAmount, 0);
  const preTaxDeductions = deductionsData.filter(d => d.preTax).reduce((sum, d) => sum + d.currentAmount, 0);
  const postTaxDeductions = deductionsData.filter(d => !d.preTax).reduce((sum, d) => sum + d.currentAmount, 0);

  // Calculate contributions
  const contributionsData = (formData.contributions || []).map(c => {
    const amount = parseFloat(c.amount) || 0;
    const currentAmount = c.isPercentage ? (grossPay * amount / 100) : amount;
    const isPreTax = c.preTax !== undefined ? c.preTax : preTaxContributionTypes.includes(c.type);
    return { ...c, currentAmount, preTax: isPreTax, name: c.type === 'other' ? c.name : c.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) };
  });
  const totalContributions = contributionsData.reduce((sum, c) => sum + c.currentAmount, 0);
  const preTaxContributions = contributionsData.filter(c => c.preTax).reduce((sum, c) => sum + c.currentAmount, 0);
  const postTaxContributions = contributionsData.filter(c => !c.preTax).reduce((sum, c) => sum + c.currentAmount, 0);

  // Combined pre-tax and post-tax totals
  const totalPreTax = preTaxDeductions + preTaxContributions;
  const totalPostTax = postTaxDeductions + postTaxContributions;

  const netPay = grossPay - totalTax - totalDeductions - totalContributions;

  // Calculate end date
  const endDate = endDateArray[stubIndex] 
    ? new Date(endDateArray[stubIndex] + 'T12:00:00')
    : (() => {
        const calculated = new Date(startDate);
        calculated.setDate(startDate.getDate() + periodLength - 1);
        return calculated;
      })();

  // Calculate pay date
  const payDate = payDateArray[stubIndex] 
    ? new Date(payDateArray[stubIndex] + 'T12:00:00')
    : nextWeekday(new Date(endDate), payDay);

  // Calculate YTD
  const ytdPayPeriods = calculatePayPeriodsFromHireDate(hireDate, endDate, periodLength);
  const ytdRegularPay = regularPay * ytdPayPeriods;
  const ytdOvertimePay = overtimePay * ytdPayPeriods;
  
  // YTD Commission should be cumulative sum of all commissions up to and including current stub
  const ytdCommission = commissionArray.slice(0, stubIndex + 1).reduce((sum, c) => sum + (c || 0), 0);
  
  // YTD Tips should be cumulative sum of all tips up to and including current stub
  const ytdTips = tipsArray.slice(0, stubIndex + 1).reduce((sum, t) => sum + (t || 0), 0);
  
  // YTD Tips for pay calculation - only non-cash tips count toward gross pay (check amount)
  const ytdTipsForPay = tipsArray.slice(0, stubIndex + 1).reduce((sum, t, i) => {
    const isCash = tipsCashArray[i] || false;
    return sum + (isCash ? 0 : (t || 0));
  }, 0);
  
  // YTD Cash Tips - for taxable income calculation
  const ytdCashTips = tipsArray.slice(0, stubIndex + 1).reduce((sum, t, i) => {
    const isCash = tipsCashArray[i] || false;
    return sum + (isCash ? (t || 0) : 0);
  }, 0);
  
  // YTD Gross Pay = (base pay * periods) + cumulative commission + cumulative non-cash tips
  // Base pay is regularPay + overtimePay (without commission and tips)
  const basePay = regularPay + overtimePay;
  const ytdGrossPay = (basePay * ytdPayPeriods) + ytdCommission + ytdTipsForPay;
  
  // YTD Taxable Income = YTD Gross Pay + YTD Cash Tips (cash tips are taxed even though not in check)
  const ytdTaxableIncome = ytdGrossPay + ytdCashTips;
  
  // YTD Taxes should be calculated on YTD Taxable Income (includes cash tips)
  // SS and Medicare are flat percentages, so calculate directly on ytdTaxableIncome
  const ytdSsTax = isContractor ? 0 : Math.round(ytdTaxableIncome * rates.ss * 100) / 100;
  const ytdMedTax = isContractor ? 0 : Math.round(ytdTaxableIncome * rates.med * 100) / 100;
  
  // For federal and state taxes, we approximate by using the average taxable income per period
  // This accounts for varying commissions and tips across periods
  const avgTaxableIncomePerPeriod = ytdTaxableIncome / ytdPayPeriods;
  let ytdFederalTax = 0;
  if (!isContractor) {
    if (formData.federalFilingStatus) {
      const avgFederalTax = calculateFederalTax(avgTaxableIncomePerPeriod, payFrequency, formData.federalFilingStatus);
      ytdFederalTax = Math.round(avgFederalTax * ytdPayPeriods * 100) / 100;
    } else {
      ytdFederalTax = Math.round(ytdTaxableIncome * rates.fedFlat * 100) / 100;
    }
  }
  
  let ytdStateTax = 0;
  if (!isContractor) {
    const avgStateTax = calculateStateTax(avgTaxableIncomePerPeriod, formData.state, payFrequency, formData.stateAllowances || 0, stateRate);
    ytdStateTax = Math.round(avgStateTax * ytdPayPeriods * 100) / 100;
  }
  
  const ytdLocalTax = isContractor ? 0 : (formData.includeLocalTax && localTaxRate > 0 ? Math.round(ytdTaxableIncome * localTaxRate * 100) / 100 : 0);
  const ytdTotalTax = Math.round((ytdSsTax + ytdMedTax + ytdFederalTax + ytdStateTax + ytdLocalTax) * 100) / 100;
  
  const ytdDeductions = Math.round(totalDeductions * ytdPayPeriods * 100) / 100;
  const ytdContributions = Math.round(totalContributions * ytdPayPeriods * 100) / 100;
  
  // Calculate YTD Pre-Tax and Post-Tax amounts
  const ytdPreTax = Math.round(totalPreTax * ytdPayPeriods * 100) / 100;
  const ytdPostTax = Math.round(totalPostTax * ytdPayPeriods * 100) / 100;
  
  const ytdNetPay = Math.round((ytdGrossPay - ytdTotalTax - ytdDeductions - ytdContributions) * 100) / 100;
  const ytdHours = (hours + overtime) * ytdPayPeriods;

  const templateData = {
    formData, hours, overtime, commission, tips, tipsCash, regularPay, overtimePay, grossPay,
    ssTax, medTax, federalTax, stateTax, localTax, localTaxRate, totalTax, netPay,
    rate, stateRate, startDate, endDate, payDate, payFrequency,
    // the rate labels and employer taxes follow the form's Tax Settings, as in the PDF
    ssRate: rates.ss, medRate: rates.med,
    sutaRate: rates.suta ?? getSUTARate(state), futaRate: rates.futa,
    stubNum: stubIndex, totalStubs,
    ytdPayPeriods, ytdRegularPay, ytdOvertimePay, ytdCommission, ytdTips, ytdGrossPay,
    ytdSsTax, ytdMedTax, ytdFederalTax, ytdStateTax, ytdLocalTax, ytdTotalTax,
    ytdNetPay, ytdHours, payType, workerType, isContractor, annualSalary,
    deductionsData, totalDeductions, preTaxDeductions, postTaxDeductions,
    contributionsData, totalContributions, preTaxContributions, postTaxContributions,
    totalPreTax, totalPostTax, ytdPreTax, ytdPostTax,
    ytdDeductions, ytdContributions,
    logoDataUrl: formData.logoDataUrl || null,
    isPreview: true,
    absencePlansData: (formData.absencePlans || []).map(plan => ({
      description: plan.description || "PTO Plan", accrued: plan.accrued || "0", reduced: plan.reduced || "0"
    })),
    employerBenefitsData: (formData.employerBenefits || []).map(b => {
      let amount;
      if (b.type === '401k_match') {
        // Calculate 401k match based on employee contribution
        const employee401k = contributionsData.find(c => c.type === '401k' || c.type === 'roth_401k');
        if (employee401k) {
          const matchUpTo = parseFloat(b.matchUpTo) || 6;
          const matchPercent = parseFloat(b.matchPercent) || 50;
          const maxMatchableAmount = grossPay * (matchUpTo / 100);
          const matchableAmount = Math.min(employee401k.currentAmount, maxMatchableAmount);
          amount = matchableAmount * (matchPercent / 100);
        } else {
          amount = 0;
        }
      } else {
        amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
      }
      return { name: b.name || "Employer Benefit", type: b.type, currentAmount: amount };
    }),
    totalEmployerBenefits: (formData.employerBenefits || []).reduce((sum, b) => {
      let amount;
      if (b.type === '401k_match') {
        const employee401k = contributionsData.find(c => c.type === '401k' || c.type === 'roth_401k');
        if (employee401k) {
          const matchUpTo = parseFloat(b.matchUpTo) || 6;
          const matchPercent = parseFloat(b.matchPercent) || 50;
          const maxMatchableAmount = grossPay * (matchUpTo / 100);
          const matchableAmount = Math.min(employee401k.currentAmount, maxMatchableAmount);
          amount = matchableAmount * (matchPercent / 100);
        } else {
          amount = 0;
        }
      } else {
        amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
      }
      return sum + amount;
    }, 0)
  };

  // Generate the template
  if (isCustomTemplate(template)) {
    const customLayout = await fetchCustomLayout(template);
    if (customLayout) { await preloadLayoutAssets(customLayout, templateData); renderLayout(doc, customLayout, templateData); }
    else await generateTemplateA(doc, templateData, pageWidth, pageHeight, margin);
  } else switch (template) {
    case 'template-b':
      generateTemplateB(doc, templateData, pageWidth, pageHeight, margin);
      break;
    case 'template-c':
      await generateTemplateC(doc, templateData, pageWidth, pageHeight, margin);
      break;
    case 'template-h':
      generateTemplateH(doc, templateData, pageWidth, pageHeight, margin);
      break;
    case 'template-a':
    default:
      await generateTemplateA(doc, templateData, pageWidth, pageHeight, margin);
      break;
  }

  // Add watermark
  await addPreviewWatermarkJsPdf(doc, pageWidth, pageHeight);

  // Apply template-specific metadata before output
  applyPdfMetadata(doc, template);

  const pdfDataUrl = doc.output('dataurlstring');
  return pdfDataUrl;
}

// Generate all preview PDFs as an array of base64 image URLs
export const generateAllPreviewPDFs = async (formData, template = 'template-a', numStubs = 1) => {
  if (template === 'template-b') template = 'template-a'; // ADP retired
  try {
    const rate = parseFloat(formData.rate) || 0;
    const annualSalary = parseFloat(formData.annualSalary) || 0;
    const payFrequency = formData.payFrequency || "biweekly";
    const periodLength = payFrequency === "biweekly" ? 14 : 7;
    const defaultHours = payFrequency === "weekly" ? 40 : 80;
    const payDay = formData.payDay || "Friday";
    const payType = formData.payType || "hourly";
    const workerType = formData.workerType || "employee";
    const isContractor = workerType === "contractor";
    const periodsPerYear = payFrequency === "weekly" ? 52 : 26;

    const hoursArray = (formData.hoursList || "").split(",").map((h) => parseFloat(h.trim()) || 0);
    const overtimeArray = (formData.overtimeList || "").split(",").map((h) => parseFloat(h.trim()) || 0);
    const commissionArray = (formData.commissionList || "").split(",").map((c) => parseFloat(c.trim()) || 0);
    const tipsArray = (formData.tipsList || "").split(",").map((t) => parseFloat(t.trim()) || 0);
    const tipsCashArray = (formData.tipsCashList || "").split(",").map((t) => t.trim() === '1');
    const startDateArray = (formData.startDateList || "").split(",").map((d) => d.trim()).filter(d => d);
    const endDateArray = (formData.endDateList || "").split(",").map((d) => d.trim()).filter(d => d);
    const payDateArray = (formData.payDateList || "").split(",").map((d) => d.trim()).filter(d => d);

    // Use consistent hire date - fall back to startDate (first stub's date) if hireDate not provided
    // This ensures YTD calculations are consistent across all stubs
    // Priority: formData.hireDate > startDateArray[0] > formData.startDate > today (last resort)
    let hireDate;
    if (formData.hireDate) {
      hireDate = new Date(formData.hireDate + 'T12:00:00');
    } else if (startDateArray[0]) {
      hireDate = new Date(startDateArray[0] + 'T12:00:00');
    } else if (formData.startDate) {
      hireDate = new Date(formData.startDate + 'T12:00:00');
    } else {
      hireDate = new Date(); // Last resort fallback
    }

    // Same anchor consistency as the main generator: a hire date after the
    // first period end would flip YTD anchors mid-batch and make YTD collapse
    // on the first stub whose end crosses it; anchor at Jan 1 for every stub.
    const firstPeriodEnd = endDateArray[0]
      ? new Date(endDateArray[0] + 'T12:00:00')
      : (() => {
          const base = startDateArray[0] || formData.startDate;
          if (!base) return null;
          const d = new Date(base + 'T12:00:00');
          d.setDate(d.getDate() + periodLength - 1);
          return d;
        })();
    if (firstPeriodEnd && hireDate > firstPeriodEnd) {
      hireDate = new Date(firstPeriodEnd.getFullYear(), 0, 1);
    }

    const state = formData.state?.toUpperCase() || "";
    const stateRate = isContractor ? 0 : (usRates(formData).state ?? getStateTaxRate(state));

    const commonData = {
      rate, annualSalary, payFrequency, periodLength, defaultHours, payDay,
      payType, workerType, isContractor, periodsPerYear, hoursArray, overtimeArray,
      commissionArray, tipsArray, tipsCashArray, startDateArray, endDateArray, payDateArray, hireDate, stateRate, state
    };

    // Generate previews for all stubs
    const previews = [];
    const actualNumStubs = Math.max(1, numStubs);
    
    for (let i = 0; i < actualNumStubs; i++) {
      const preview = await generateSingleStubPreview(formData, template, i, actualNumStubs, commonData);
      previews.push(preview);
    }

    return previews;
  } catch (error) {
    console.error("Error generating all previews:", error);
    return [null];
  }
};

// Same as generateAllPreviewPDFs but rasterizes each stub to a PNG image data
// URL (via pdf.js), for surfaces that render previews in an <img> — e.g. the
// app's template cards and preview modal. (The web generator keeps the PDF form
// because it renders previews in an <iframe>.)
export const generateAllPreviewImages = async (formData, template = 'template-a', numStubs = 1) => {
  await fetchTaxRates(); // the admin's per-year tax config, before any math
  const pdfs = await generateAllPreviewPDFs(formData, template, numStubs);
  const images = [];
  for (const pdf of pdfs) {
    try {
      images.push(pdf ? await convertPdfToImage(pdf) : null);
    } catch (error) {
      console.error("Error converting preview to image:", error);
      images.push(null);
    }
  }
  return images;
};

// Generate preview PDF as base64 data URL (legacy - returns first stub only)
export const generatePreviewPDF = async (formData, template = 'template-a') => {
  if (template === 'template-b') template = 'template-a'; // ADP retired
  await fetchTaxRates(); // the admin's per-year tax config, before any math
  try {
    const rate = parseFloat(formData.rate) || 0;
    const annualSalary = parseFloat(formData.annualSalary) || 0;
    const payFrequency = formData.payFrequency || "biweekly";
    const periodLength = payFrequency === "biweekly" ? 14 : 7;
    const defaultHours = payFrequency === "weekly" ? 40 : 80;
    const payDay = formData.payDay || "Friday";
    const payType = formData.payType || "hourly";
    const workerType = formData.workerType || "employee";
    const isContractor = workerType === "contractor";

    const periodsPerYear = payFrequency === "weekly" ? 52 : 26;

    const hoursArray = (formData.hoursList || "")
      .split(",")
      .map((h) => parseFloat(h.trim()) || 0);
    const overtimeArray = (formData.overtimeList || "")
      .split(",")
      .map((h) => parseFloat(h.trim()) || 0);
    const commissionArray = (formData.commissionList || "")
      .split(",")
      .map((c) => parseFloat(c.trim()) || 0);
    const tipsArray = (formData.tipsList || "")
      .split(",")
      .map((t) => parseFloat(t.trim()) || 0);
    const tipsCashArray = (formData.tipsCashList || "")
      .split(",")
      .map((t) => t.trim() === '1');
    const startDateArray = (formData.startDateList || "")
      .split(",")
      .map((d) => d.trim())
      .filter(d => d);
    const endDateArray = (formData.endDateList || "")
      .split(",")
      .map((d) => d.trim())
      .filter(d => d);
    const payDateArray = (formData.payDateList || "")
      .split(",")
      .map((d) => d.trim())
      .filter(d => d);

    // Use consistent hire date - fall back to startDate (first stub's date) if hireDate not provided
    // Priority: formData.hireDate > startDateArray[0] > formData.startDate > today (last resort)
    let hireDate;
    if (formData.hireDate) {
      hireDate = new Date(formData.hireDate + 'T12:00:00');
    } else if (startDateArray[0]) {
      hireDate = new Date(startDateArray[0] + 'T12:00:00');
    } else if (formData.startDate) {
      hireDate = new Date(formData.startDate + 'T12:00:00');
    } else {
      hireDate = new Date(); // Last resort fallback
    }
    // Use custom start date from first period if available, otherwise use formData.startDate
    const startDate = startDateArray[0] 
      ? new Date(startDateArray[0] + 'T12:00:00') 
      : (formData.startDate ? new Date(formData.startDate + 'T12:00:00') : new Date(hireDate));

    // Get state tax rate from centralized tax calculator
    const state = formData.state?.toUpperCase() || "";
    const stateRate = isContractor ? 0 : (usRates(formData).state ?? getStateTaxRate(state));

    // Create PDF document
    const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;

    // Calculate pay values for preview (first stub)
    let hours = 0;
    let overtime = 0;
    let regularPay = 0;
    let overtimePay = 0;
    let commission = commissionArray[0] || 0;
    let tips = tipsArray[0] || 0;
    let tipsCash = tipsCashArray[0] || false;
    let grossPay = 0;
    
    // Cash tips are shown but NOT included in gross pay (employee already received them)
    const tipsForPay = tipsCash ? 0 : tips;

    if (payType === "salary") {
      grossPay = annualSalary / periodsPerYear + commission + tipsForPay;
      regularPay = annualSalary / periodsPerYear;
      hours = defaultHours;
      overtime = 0;
      overtimePay = 0;
    } else {
      hours = hoursArray[0] || defaultHours;
      overtime = overtimeArray[0] || 0;
      regularPay = rate * hours;
      overtimePay = rate * 1.5 * overtime;
      grossPay = regularPay + overtimePay + commission + tipsForPay;
    }

    const pdfRates = usRates(formData);
    const ssTax = isContractor ? 0 : grossPay * pdfRates.ss;
    const medTax = isContractor ? 0 : grossPay * pdfRates.med;
    
    // Calculate federal income tax based on filing status (no allowances per 2020+ W-4)
    let federalTax = 0;
    if (!isContractor) {
      if (formData.federalFilingStatus) {
        federalTax = calculateFederalTax(
          grossPay,
          payFrequency,
          formData.federalFilingStatus
        );
      } else {
        federalTax = grossPay * pdfRates.fedFlat;
      }
    }
    
    // Calculate state tax with allowances (only for applicable states)
    let stateTax = 0;
    if (!isContractor) {
      stateTax = calculateStateTax(
        grossPay,
        formData.state,
        payFrequency,
        formData.stateAllowances || 0,
        stateRate
      );
    }
    
    // Get actual local tax rate
    const localTaxRate = getEffectiveLocalTaxRate(formData);
    const localTax = isContractor ? 0 : (formData.includeLocalTax && localTaxRate > 0 ? grossPay * localTaxRate : 0);
    const totalTax = ssTax + medTax + federalTax + stateTax + localTax;

    // Calculate deductions for preview
    const deductionsData = (formData.deductions || []).map(d => {
      const amount = parseFloat(d.amount) || 0;
      const currentAmount = d.isPercentage ? (grossPay * amount / 100) : amount;
      return {
        ...d,
        currentAmount,
        name: d.type === 'other' ? d.name : d.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      };
    });
    const totalDeductions = deductionsData.reduce((sum, d) => sum + d.currentAmount, 0);

    // Calculate contributions for preview
    const contributionsData = (formData.contributions || []).map(c => {
      const amount = parseFloat(c.amount) || 0;
      const currentAmount = c.isPercentage ? (grossPay * amount / 100) : amount;
      return {
        ...c,
        currentAmount,
        name: c.type === 'other' ? c.name : c.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      };
    });
    const totalContributions = contributionsData.reduce((sum, c) => sum + c.currentAmount, 0);

    const netPay = grossPay - totalTax - totalDeductions - totalContributions;

    // Use custom end date from first period if available, otherwise calculate from start date
    const endDate = endDateArray[0] 
      ? new Date(endDateArray[0] + 'T12:00:00')
      : (() => {
          const calculated = new Date(startDate);
          calculated.setDate(startDate.getDate() + periodLength - 1);
          return calculated;
        })();
    // Use custom pay date if available, otherwise calculate from end date
    const payDate = payDateArray[0] 
      ? new Date(payDateArray[0] + 'T12:00:00')
      : nextWeekday(new Date(endDate), payDay);

    // Calculate YTD
    const ytdPayPeriods = calculatePayPeriodsFromHireDate(hireDate, endDate, periodLength);
    const ytdRegularPay = regularPay * ytdPayPeriods;
    const ytdOvertimePay = overtimePay * ytdPayPeriods;
    const ytdCommission = commission * ytdPayPeriods;
    const ytdTips = tips * ytdPayPeriods;
    const ytdGrossPay = grossPay * ytdPayPeriods;
    const ytdSsTax = ssTax * ytdPayPeriods;
    const ytdMedTax = medTax * ytdPayPeriods;
    const ytdFederalTax = federalTax * ytdPayPeriods;
    const ytdStateTax = stateTax * ytdPayPeriods;
    const ytdLocalTax = localTax * ytdPayPeriods;
    const ytdTotalTax = totalTax * ytdPayPeriods;
    const ytdDeductions = totalDeductions * ytdPayPeriods;
    const ytdContributions = totalContributions * ytdPayPeriods;
    const ytdNetPay = netPay * ytdPayPeriods;
    const ytdHours = (hours + overtime) * ytdPayPeriods;

    const templateData = {
      formData,
      hours,
      overtime,
      commission,
      tips,
      tipsCash,
      regularPay,
      overtimePay,
      grossPay,
      ssTax,
      medTax,
      federalTax,
      stateTax,
      localTax,
      localTaxRate,
      totalTax,
      netPay,
      rate,
      stateRate,
      ssRate: rates.ss,
      medRate: rates.med,
      // the employer taxes, as the PDF computes them
      sutaRate: rates.suta ?? getSUTARate(formData.state),
      futaRate: rates.futa,
      startDate,
      endDate,
      payDate,
      payFrequency,
      stubNum: 0,
      totalStubs: 1,
      ytdPayPeriods,
      ytdRegularPay,
      ytdOvertimePay,
      ytdCommission,
      ytdTips,
      ytdGrossPay,
      ytdSsTax,
      ytdMedTax,
      ytdFederalTax,
      ytdStateTax,
      ytdLocalTax,
      ytdTotalTax,
      ytdNetPay,
      ytdHours,
      payType,
      workerType,
      isContractor,
      annualSalary,
      // Deductions and contributions
      deductionsData,
      totalDeductions,
      contributionsData,
      totalContributions,
      ytdDeductions,
      ytdContributions,
      // Pass the logo for preview - use uploaded logo if available
      logoDataUrl: formData.logoDataUrl || null,
      isPreview: true,
      // Absence plans for Template C (Workday)
      absencePlansData: (formData.absencePlans || []).map(plan => ({
        description: plan.description || "PTO Plan",
        accrued: plan.accrued || "0",
        reduced: plan.reduced || "0"
      })),
      // Employer benefits for Template C (Workday)
      employerBenefitsData: (formData.employerBenefits || []).map(b => {
        const amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
        return { name: b.name || "Employer Benefit", type: b.type, currentAmount: amount };
      }),
      totalEmployerBenefits: (formData.employerBenefits || []).reduce((sum, b) => {
        const amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
        return sum + amount;
      }, 0)
    };

    // Generate the template based on selection
    if (isCustomTemplate(template)) {
      const customLayout = await fetchCustomLayout(template);
      if (customLayout) { await preloadLayoutAssets(customLayout, templateData); renderLayout(doc, customLayout, templateData); }
      else await generateTemplateA(doc, templateData, pageWidth, pageHeight, margin);
    } else switch (template) {
      case 'template-b':
        generateTemplateB(doc, templateData, pageWidth, pageHeight, margin);
        break;
      case 'template-c':
        await generateTemplateC(doc, templateData, pageWidth, pageHeight, margin);
        break;
      case 'template-h':
        generateTemplateH(doc, templateData, pageWidth, pageHeight, margin);
        break;
      case 'template-a':
      default:
        await generateTemplateA(doc, templateData, pageWidth, pageHeight, margin);
        break;
    }

    // Add watermark on ALL pages
    await addPreviewWatermarkJsPdf(doc, pageWidth, pageHeight);

    // Convert to base64 data URL (PDF)
    const pdfDataUrl = doc.output('dataurlstring');
    
    // Convert PDF to image for preview display
    const imageDataUrl = await convertPdfToImage(pdfDataUrl);
    
    return imageDataUrl;
  } catch (error) {
    console.error("Error generating preview:", error);
    return null;
  }
};
